import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import GetData from "../../../services/getData";
import logPatientChange from "../../../services/patientLogs";
import { buildPatientLogText } from "../../../services/patientLogMessages";
import "./patientInbody.css";
import "../patients.css";
import BlockTitle from "../../../components/layout/blockTitle/BlockTitle";
import EditableModal from "../../../components/layout/editableModal/EditableModal";
import Toast from "../../../components/layout/toast/Toast";
import { Col, Container, Row } from "react-bootstrap";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

const getDynamicsModalFields = (t) => [
  { name: "weight", label: t("dynamics.fields.weight"), type: "number" },
  { name: "blood_pressure", label: t("dynamics.fields.bloodPressure"), type: "text" },
  // { name: "chest_circumference_cm", label: "Объем груди (см)", type: "number" },
  // { name: "waist_circumference_cm", label: "Объем талии (см)", type: "number" },
  // { name: "hips_circumference_cm", label: "Объем бедер (см)", type: "number" },
  { name: "ideal_weight", label: t("dynamics.fields.idealWeight"), type: "number" },
  { name: "inspection", label: t("dynamics.fields.inspection"), type: "textarea" },
];

const getInitialDynamicsData = (fields) =>
  Object.fromEntries(fields.map((f) => [f.name, ""]));

const normalizeComparableValue = (value) =>
  value == null ? "" : String(value).trim();

const normalizeDisplayValue = (value) => {
  const prepared = String(value ?? "").trim();
  return prepared || "—";
};

const isValidBloodPressure = (value) => {
  const prepared = String(value ?? "").trim();
  if (!prepared) return true;
  return /^\d{2,3}\s*\/\s*\d{2,3}$/.test(prepared);
};

const buildDynamicsChanges = (before, after, fields) =>
  fields.reduce((acc, field) => {
    const fromValue = normalizeComparableValue(before?.[field.name]);
    const toValue = normalizeComparableValue(after?.[field.name]);
    if (fromValue !== toValue) {
      acc.push({
        label: field.label,
        from: before?.[field.name],
        to: after?.[field.name],
      });
    }
    return acc;
  }, []);

function PatientInbody() {
  const { t } = useTranslation("patient");
  const dynamicsModalFields = useMemo(() => getDynamicsModalFields(t), [t]);
  const initialDynamicsData = useMemo(
    () => getInitialDynamicsData(dynamicsModalFields),
    [dynamicsModalFields],
  );

  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingDynamics, setEditingDynamics] = useState(null);
  const [dynamics, setDynamics] = useState([]);
  const [toast, setToast] = useState({});

  const { patientId } = useParams();
  const id_patient = Number(patientId);

  const fetchDynamics = async (id_patient) => {
    const data = await GetData({
      module: "patient",
      block: "indicators",
      id_patient: id_patient,
    });

    if (data && Array.isArray(data.result)) {
      setDynamics(data.result);
    } else if (data && data.result) {
      setDynamics([data.result]);
    } else {
      setDynamics([]);
    }
  };

  useEffect(() => {
    fetchDynamics(id_patient);
  }, [id_patient]);

  const blockTitleButtons = [
    { label: t("analyses.buttons.add"), onClick: () => setShowAddModal(true), type: "light" },
  ];

  const showToast = (title, status = "success") => {
    setToast({
      id: Date.now(),
      title,
      status,
    });
  };

  const handleSaveDynamics = async (formData) => {
    if (!isValidBloodPressure(formData?.blood_pressure)) {
      showToast(t("dynamics.invalidPressure"), "error");
      return;
    }

    try {
      const response = await GetData({
        module: "patient",
        block: "indicators",
        action: "add",
        id_patient: id_patient,
        ...formData,
      });
      if (response?.status?.success === false) {
        showToast(response?.status?.message || t("dynamics.toast.addFailed"), "error");
        return;
      }
      await logPatientChange({
        idPatient: id_patient,
        text: buildPatientLogText("patient_dynamics_added", {
          details: dynamicsModalFields
            .map((field) => `${field.label}: ${normalizeDisplayValue(formData?.[field.name])}`)
            .join("; "),
        }),
      });
      setShowAddModal(false);
      fetchDynamics(id_patient);
      showToast(response?.message || response?.status?.message || t("dynamics.toast.added"), "success");
    } catch (err) {
      console.error("Ошибка сохранения оценки состава тела:", err);
      showToast(t("dynamics.toast.addFailed"), "error");
    }
  };

  const openEditDynamicsModal = (row) => {
    setEditingDynamics(row);
    setShowEditModal(true);
  };

  const handleUpdateDynamics = async (formData) => {
    if (!editingDynamics?.id) return;
    if (!isValidBloodPressure(formData?.blood_pressure)) {
      showToast(t("dynamics.invalidPressure"), "error");
      return;
    }

    try {
      const response = await GetData({
        module: "patient",
        block: "indicators",
        action: "update",
        id_patient: id_patient,
        id_dynamics: editingDynamics.id,
        id: editingDynamics.id,
        ...formData,
      });

      if (response?.status?.success === false) {
        showToast(response?.status?.message || t("dynamics.toast.updateFailed"), "error");
        return;
      }

      await logPatientChange({
        idPatient: id_patient,
        idItem: editingDynamics.id,
        text: buildPatientLogText("patient_dynamics_updated", {
          changes: buildDynamicsChanges(editingDynamics, formData, dynamicsModalFields),
        }),
      });
      setShowEditModal(false);
      setEditingDynamics(null);
      fetchDynamics(id_patient);
      showToast(response?.message || response?.status?.message || t("dynamics.toast.updated"), "success");
    } catch (err) {
      console.error("Ошибка обновления оценки состава тела:", err);
      showToast(t("dynamics.toast.updateFailed"), "error");
    }
  };

  const handleDeleteDynamics = async (rowId) => {
    if (!rowId) return;

    const isConfirmed = window.confirm(t("dynamics.confirmDelete"));
    if (!isConfirmed) return;

    try {
      const response = await GetData({
        module: "patient",
        block: "indicators",
        action: "delete",
        id_patient: id_patient,
        id_dynamics: rowId,
        id: rowId,
      });

      if (response?.status?.success === false) {
        showToast(response?.status?.message || t("dynamics.toast.deleteFailed"), "error");
        return;
      }

      await logPatientChange({
        idPatient: id_patient,
        idItem: rowId,
        text: buildPatientLogText("patient_dynamics_deleted", {
          details: ``,
        }),
      });
      fetchDynamics(id_patient);
      showToast(response?.message || response?.status?.message || t("dynamics.toast.deleted"), "success");
    } catch (err) {
      console.error("Ошибка удаления оценки состава тела:", err);
      showToast(t("dynamics.toast.deleteFailed"), "error");
    }
  };

  const formatDateTime = (dateTimeStr) => {
    if (!dateTimeStr) return "—";

    const [datePart, timePart] = dateTimeStr.split(" ");
    if (!datePart) return dateTimeStr;

    const [year, month, day] = datePart.split("-");
    if (!year || !month || !day) return dateTimeStr;

    return `${day}.${month}.${year} ${timePart || ""}`.trim();
  };

  const formatShortDate = (dateTimeStr) => {
    if (!dateTimeStr) return "—";
    const [datePart] = String(dateTimeStr).split(" ");
    const [year, month, day] = String(datePart).split("-");
    if (!year || !month || !day) return String(dateTimeStr);
    return `${day}.${month}`;
  };

  const sortedDynamics =
    dynamics && dynamics.length
      ? [...dynamics].sort((a, b) => {
          const ad = a?.date_created ? Date.parse(a.date_created) : NaN;
          const bd = b?.date_created ? Date.parse(b.date_created) : NaN;
          if (!Number.isNaN(ad) && !Number.isNaN(bd)) return bd - ad; // новые сверху
          return 0;
        })
      : [];

  const latestDynamics = sortedDynamics.length ? sortedDynamics[0] : null;

  const chartData = sortedDynamics
    .slice()
    .reverse()
    .map((d) => ({
      id: d?.id ?? d?.date_created,
      date_created: d?.date_created,
      dateLabel: formatShortDate(d?.date_created),
      weight: d?.weight != null && d?.weight !== "" ? Number(d.weight) : null,
    }))
    .filter((row) => row.date_created);

  return (
    <div className="patient-dynamics">
      {toast.title ? <Toast key={toast.id} title={toast.title} status={toast.status} /> : false}
      <Row>
          <div className="patient-name-title-sticky">
            <BlockTitle title={t("dynamics.title")} buttons={blockTitleButtons} />
          </div>
        <Col>
          <div className="history-section mt-4">
            {chartData.length >= 2 && (
              <div className="chart-container main-chart">
                <div style={{ width: "100%", height: 320 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={chartData} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis
                        dataKey="dateLabel"
                        tick={{ fontSize: 12 }}
                        interval="preserveStartEnd"
                      />
                      <YAxis tick={{ fontSize: 12 }} width={44} tickFormatter={(v) => (v == null ? "" : v)} />
                      <Tooltip
                        labelFormatter={(_, payload) =>
                          payload?.[0]?.payload?.date_created
                            ? formatDateTime(payload[0].payload.date_created)
                            : "—"
                        }
                        formatter={(value, name) => {
                          if (value == null || Number.isNaN(Number(value))) return ["—", name];
                          return [value, name];
                        }}
                      />
                      <Legend />
                      <Line
                        type="monotone"
                        dataKey="weight"
                        name={t("dynamics.fields.weight")}
                        stroke="var(--brand-color)"
                        strokeWidth={2}
                        dot={false}
                        connectNulls
                        isAnimationActive
                        animationDuration={900}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}

            <div className="history-table-container">
              <table className="history-table">
                <thead>
                  <tr>
                    <th>{t("dynamics.columns.date")}</th>
                    <th>{t("dynamics.columns.bloodPressure")}</th>
                    <th>{t("dynamics.columns.weight")}</th>
                    {/* <th>Объем груди (см)</th> */}
                    {/* <th>Объем талии (см)</th> */}
                    {/* <th>Объем бедер (см)</th> */}
                    <th>{t("dynamics.columns.inspection")}</th>
                    <th>{t("dynamics.columns.actions")}</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedDynamics.length ? (
                    sortedDynamics.map((row) => {
                      const isLatest = String(row?.id) === String(latestDynamics?.id);
                      const inspectionValue = String(row?.inspection ?? "").trim();
                      return (
                        <tr
                          key={row?.id ?? row?.date_created}
                          className="history-row"
                        >
                          <td>
                            {formatDateTime(row?.date_created)}
                            {isLatest && <span className="current-badge">{t("dynamics.latest")}</span>}
                          </td>
                          <td>{row?.blood_pressure ?? "—"}</td>
                          <td>{row?.weight ?? "—"}</td>
                          {/* <td>{row?.chest_circumference_cm ?? "—"}</td> */}
                          {/* <td>{row?.waist_circumference_cm ?? "—"}</td> */}
                          {/* <td>{row?.hips_circumference_cm ?? "—"}</td> */}
                          <td style={{ whiteSpace: "pre-wrap" }}>{inspectionValue || "—"}</td>
                          <td>
                            <div className="dynamics-row-actions">
                              <button
                                type="button"
                                className="dynamics-action-btn dynamics-action-btn--edit"
                                onClick={() => openEditDynamicsModal(row)}
                                title={t("dynamics.edit")}
                                aria-label={t("dynamics.edit")}
                              >
                                <svg viewBox="0 0 24 24" aria-hidden="true">
                                  <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zm2.92 2.33H5v-.92l9.06-9.06.92.92L5.92 19.58zM20.71 7.04a1.003 1.003 0 0 0 0-1.42L18.37 3.29a1.003 1.003 0 0 0-1.42 0l-1.83 1.83 3.75 3.75 1.84-1.83z" />
                                </svg>
                              </button>
                              <button
                                type="button"
                                className="dynamics-action-btn dynamics-action-btn--delete"
                                onClick={() => handleDeleteDynamics(row?.id)}
                                title={t("dynamics.delete")}
                                aria-label={t("dynamics.delete")}
                              >
                                <svg viewBox="0 0 24 24" aria-hidden="true">
                                  <path d="M6 7h12v2H6V7zm2 3h8l-1 10H9L8 10zm3-6h2l1 1h4v2H6V5h4l1-1z" />
                                </svg>
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan={5} className="empty-state">
                        {t("dynamics.empty")}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </Col>
      </Row>

      <EditableModal
        show={showAddModal}
        onHide={() => setShowAddModal(false)}
        title={t("dynamics.addTitle")}
        initialData={initialDynamicsData}
        fields={dynamicsModalFields}
        onSave={handleSaveDynamics}
        closeOnSave={false}
      />

      <EditableModal
        show={showEditModal}
        onHide={() => {
          setShowEditModal(false);
          setEditingDynamics(null);
        }}
        title={t("dynamics.editTitle")}
        initialData={
          editingDynamics
            ? Object.fromEntries(
                dynamicsModalFields.map((f) => [f.name, editingDynamics?.[f.name] ?? ""]),
              )
            : initialDynamicsData
        }
        fields={dynamicsModalFields}
        onSave={handleUpdateDynamics}
        closeOnSave={false}
      />
    </div>
  );
}

export default PatientInbody;
